<script setup lang="ts">
import { useI18n } from '#i18n';
import { XIcon, XButton } from '@clicksign/excalibur';

import { SecureEnvironment } from '~~base-ui';

import CapturePreview from '../CapturePreview/CapturePreview.vue';
import CafDocumentCamera from '../CafDocumentCamera/CafDocumentCamera.vue';
import { DocumentObjType, buildDocumentTypeTexts } from '../../constants/facialBiometrics';
import { appendFacematchPhoto, appendDocumentscopyPhoto } from '../../services';

type TemplateType = {
  [key: string]: any;
};

type DocumentUrlsType = {
  front: string,
  back: string
};

type DocumentDetectorCaptureProps = {
  isDocumentscopy?: boolean,
  documentType?: string,
  documents?: DocumentObjType,
  documentation?: string,
  facematchKey?: string,
  documentscopyKey?: string,
  signatureRequestKey: string,
  token?: string,
  environment?: string
};

type DocumentSelectorEmit = {
  (event: 'close'): void;
  (event: 'finish-capture', value: DocumentObjType): void;
  (event: 'error-capture', value: string): void;
};

const props = withDefaults(defineProps<DocumentDetectorCaptureProps>(), {
  isDocumentscopy: false,
  documentscopyKey: '',
  facematchKey: '',
  documentType: 'RG',
  documents: () => ({ front: undefined, back: undefined }),
  documentation: '',
});

const emit = defineEmits<DocumentSelectorEmit>();

const gtm = useGtm();

const step = ref(0);
const showCapture = ref(false);
const { documentType, documents, facematchKey, documentscopyKey, isDocumentscopy, signatureRequestKey } = toRefs(props);

const preview = reactive({
  openPreview: false,
  captureError: false,
  alreadyCaptured: false,
  image: undefined,
  loading: false,
}) as {
  openPreview: boolean | undefined,
  captureError: boolean | undefined,
  alreadyCaptured: boolean | undefined,
  image: Blob | undefined,
  loading: boolean | undefined,
};

const document: DocumentObjType = reactive({
  front: undefined,
  back: undefined,
});

const documentUrls: DocumentUrlsType = reactive({
  front: '',
  back: '',
});

const isFirstCapture = computed(() => step.value === 0);

const isCaptureIncomplete = ref(false);

const sendDocumentPhoto = async (image: Blob | undefined, position: string) => {
  if (image) {
    await appendFacematchPhoto(
      signatureRequestKey.value,
      facematchKey.value,
      image,
      'document',
      position,
    );
  }
};

const sendDocumentscopyPhoto = async (imageUrl: string | undefined, position: string) => {
  if (imageUrl) {
    await appendDocumentscopyPhoto(
      signatureRequestKey.value,
      documentscopyKey.value,
      imageUrl,
      position,
    );
  }
};

const checkDocumentWasCaptured = () => {
  const { front, back } = documents.value;

  const currentDocumentToCheck = isFirstCapture.value ? front : back;

  if (currentDocumentToCheck) {
    showCapture.value = false;
    preview.image = currentDocumentToCheck;
    preview.openPreview = true;
    preview.alreadyCaptured = true;
  } else showCapture.value = true;
};

const handleNextStep = async () => {
  preview.alreadyCaptured = false;
  preview.loading = true;

  if (isFirstCapture.value) {
    if (isDocumentscopy.value) {
      await sendDocumentscopyPhoto(documentUrls.front, 'front');
    } else {
      await sendDocumentPhoto(document.front, 'front');
    }
    step.value += 1;
    showCapture.value = true;
    preview.image = undefined;
    preview.openPreview = false;
    preview.captureError = false;
    checkDocumentWasCaptured();
  } else {
    if (isDocumentscopy.value) {
      await sendDocumentscopyPhoto(documentUrls.back, 'back');
      emit('finish-capture', document);

      return;
    }
    await sendDocumentPhoto(document.back, 'back');

    emit('finish-capture', document);
  }
  preview.loading = false;
};

const handleTryAgain = () => {
  preview.image = undefined;
  preview.openPreview = false;
  preview.loading = false;
  preview.captureError = false;
  showCapture.value = true;
  gtm?.trackEvent({
    event: 'ga-send',
    ga_event_category: 'button-try-again-documentscopy',
    ga_event_name: 'documentscopy-photo-review',
  });
};

const { t } = useI18n();

const title = computed(() => {
  const documentTypeTexts = buildDocumentTypeTexts(t);
  const alreadyCapturedMessage = preview.alreadyCaptured && t('requirement.facialBiometrics.capture.title.already');
  const currentDocumentTexts = (documentTypeTexts as TemplateType)[documentType.value];
  const errorMessage = preview.captureError && t('requirement.facialBiometrics.error.capture.title');

  return alreadyCapturedMessage || errorMessage || currentDocumentTexts[step.value];
});

const captureErrorMessage = computed(() => t(`requirement.facialBiometrics.error.capture.${isFirstCapture.value ? 'front' : 'back'}`));

const loadingCameraMessage = computed(() => t('requirement.facialBiometrics.capture.loading'));

const setCapture = () => {
  showCapture.value = false;
  preview.loading = false;
  preview.openPreview = true;
};

const handleCaptureError = (image: Blob | undefined) => {
  preview.captureError = true;
  preview.image = image;
  showCapture.value = false;
  preview.openPreview = true;
};

const handleCapture = (image: Blob) => {
  preview.image = image;
  setCapture();
  if (preview.captureError) return;
  if (isFirstCapture.value) document.front = image;
  else document.back = image;
};

const handleCaptureImageUrl = ({ imageUrl, image }: { imageUrl: string, image: Blob }) => {
  preview.image = image;
  setCapture();
  if (preview.captureError) return;

  if (isFirstCapture.value) {
    document.front = image;
    documentUrls.front = imageUrl;
  } else {
    document.back = image;
    documentUrls.back = imageUrl;
  }
};

const handleCameraError = (error: string) => {
  emit('error-capture', error);
};

const canCloseDocumentCapture = computed(() => showCapture.value && !preview.loading);

onMounted(() => {
  checkDocumentWasCaptured();
  isCaptureIncomplete.value = !unref(documents).front || !unref(documents).back;
});
</script>

<template>
  <div :class="$style.overlayContainer">
    <h4 :class="$style.title">
      {{ title }}
    </h4>

    <XButton
      v-if="canCloseDocumentCapture"
      :class="$style.closeButton"
      data-testid="documentDetectorCaptureCloseButton"
      @click="$emit('close')"
    >
      {{ $t('common.close') }}
      <XIcon
        right
        icon="times"
      />
    </XButton>

    <div :class="$style.overlay">
      <CapturePreview
        v-if="preview.openPreview"
        :has-error="preview.captureError"
        :error-message="captureErrorMessage"
        :is-loading="preview.loading"
        :success-message="$t('requirement.facialBiometrics.capture.success')"
        :picture="preview.image"
        data-testid="cafCapturePreview"
        @try-again="handleTryAgain"
        @next="handleNextStep"
      />
      <CafDocumentCamera
        v-if="isCaptureIncomplete"
        v-show="showCapture"
        :loading-message="loadingCameraMessage"
        :document-type="documentType"
        :person-id="documentation"
        :show-capture="showCapture"
        :token="token"
        :environment="environment"
        data-testid="cafCaptureCamera"
        :step="step"
        @capture-image="handleCapture"
        @capture-image-url="handleCaptureImageUrl"
        @capture-error="handleCaptureError"
        @request-error="handleCaptureError"
        @camera-error="handleCameraError"
      />
    </div>

    <SecureEnvironment
      v-if="preview.openPreview"
      :class="$style.secureEnvironment"
    />
  </div>
</template>

<style lang="scss" module>
.overlayContainer {
  position: fixed;
  z-index: var(--zindex-modal);
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  flex-direction: column;
  width: 100vw;
  height: 100vh;
  padding-top: var(--space-large-xxx);

  &::before {
    position: absolute;
    z-index: -1;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    content: "";

    /**
    * ISSUE: https://github.com/clicksign/excalibur/issues/814
    * TODO: remove opacidade e atualiza variável do background
    */
    background: var(--color-transparent-1000-95);
  }

  .title {
    font-size: var(--font-size-large);
    line-height: var(--line-height-small);
    color: var(--color-neutral-0);
  }

  .overlay {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
  }

  .closeButton[type="button"] {
    position: absolute;
    top: 0;
    right: 0;
    color: var(--color-neutral-0);
    background-color: transparent;
    border: 0;
  }

  .secureEnvironment {
    margin-top: var(--space-regular);
    color: var(--color-neutral-0);
  }
}

</style>
